// src/components/AboutGeneratorModal.js
import React, { useState } from 'react';
import { X, Sparkles, ChevronLeft, Loader2 } from 'lucide-react';
import { generateAiContent } from '../services/ai-content-service';

const AboutGeneratorModal = ({ isOpen, onClose, onAboutGenerated }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    services: '',
    journey: '',
    strengths: '',
    purpose: '',
    training: '',
    story: '',
    approach: '',
    specializations: ''
  });
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generatedAbout, setGeneratedAbout] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const styles = [
    {
      id: 'professional',
      name: 'Professional & Credible',
      description: 'Experienced practitioner focused on results and transformation'
    },
    {
      id: 'warm',
      name: 'Warm & Approachable',
      description: 'Friendly guide who creates a safe space for your journey'
    },
    {
      id: 'mystical',
      name: 'Mystical & Spiritual',
      description: 'Intuitive healer connected to ancient wisdom and energy'
    }
  ];

  const questions = [
    {
      id: 'services',
      label: 'What spiritual services do you offer or plan to offer?',
      placeholder: 'Example: "I offer tarot readings, energy healing, and spiritual coaching"',
      helper: 'Just write naturally - 1-3 sentences is perfect',
      required: true
    },
    {
      id: 'journey',
      label: 'Tell us about your journey with these practices. Are you just starting out, or have you been doing this for a while?',
      placeholder: 'Share your journey...',
      helper: 'Whether you\'re brand new or have decades of experience, every practitioner\'s journey is unique. Share yours!',
      required: true
    },
    {
      id: 'strengths',
      label: 'What are you naturally good at when working with clients?',
      placeholder: 'What comes naturally to you?',
      helper: 'Maybe you create a safe, welcoming space. Maybe you have strong intuition. What comes naturally?',
      required: true
    },
    {
      id: 'purpose',
      label: 'Why do you do this work? What do you hope to bring to the people you work with?',
      placeholder: 'What lights you up about this?',
      helper: 'What difference do you hope to make?',
      required: true
    },
    {
      id: 'training',
      label: 'Do you have any formal training, certifications, or mentors who shaped your practice?',
      placeholder: 'Example: "I studied Reiki with Master Teacher Jane Smith" or "I\'m self-taught but have been deeply studying tarot for over a decade"',
      helper: 'This is optional - many gifted practitioners are self-taught. Both paths are valid!',
      required: false
    },
    {
      id: 'story',
      label: 'What\'s your personal story? What led you to this work?',
      placeholder: 'Share your journey...',
      helper: 'This is where you share your journey - the moment you discovered your gifts, why this matters to you.',
      required: true
    },
    {
      id: 'approach',
      label: 'How do you approach sessions with clients? What\'s your philosophy?',
      placeholder: 'Your approach and philosophy...',
      helper: 'Are you gentle and supportive? Direct and honest? What can clients expect?',
      required: true
    },
    {
      id: 'specializations',
      label: 'Are there specific areas where you excel or love to focus?',
      placeholder: 'Example: "I specialize in helping people navigate major life transitions"',
      helper: 'This could be relationship guidance, career transitions, spiritual awakening, or anything else.',
      required: false
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const canProceedToStyles = () => {
    // Check if all required questions are answered
    const requiredAnswered = questions
      .filter(q => q.required)
      .every(q => answers[q.id].trim().length > 10);
    return requiredAnswered;
  };

  // Helper function for better mock generation
  const generateMockAbout = (answers, style) => {
    // Extract key info without repetition
    const services = answers.services.replace(/^(I offer |I provide |I do )/i, '').trim();
    const journey = answers.journey.replace(/^(I'?m |I have |I've )/i, '').trim();
    const strengths = answers.strengths.replace(/^(I'?m |I have |I am )/i, '').trim();
    const purpose = answers.purpose.replace(/^(I want to |I hope to |I help )/i, '').trim();
    const story = answers.story.trim();
    const approach = answers.approach.replace(/^(I'?m |I have |I am )/i, '').trim();
    const training = answers.training ? answers.training.trim() : null;
    const specializations = answers.specializations ? answers.specializations.replace(/^(I specialize in |I focus on )/i, '').trim() : null;

    if (style === 'professional') {
      let about = `${story}\n\n`;
      about += `${journey}. Through this journey, I've developed expertise in ${strengths.toLowerCase()}, allowing me to provide effective, results-oriented guidance to my clients.\n\n`;
      if (training) {
        about += `${training}. This foundation informs my practice and ensures clients receive professional, well-grounded support.\n\n`;
      }
      about += `I specialize in ${services}, with a particular focus on ${purpose.toLowerCase()}. My methodology is ${approach.toLowerCase()}, combining proven techniques with deep spiritual understanding to create lasting transformation.\n\n`;
      if (specializations) {
        about += `I particularly excel when working with ${specializations.toLowerCase()}, where my experience and training come together to provide exceptional support.\n\n`;
      }
      about += `In our sessions, you can expect a structured yet intuitive approach that honors both your practical needs and spiritual growth. I bring professionalism, expertise, and genuine care to every interaction, creating a framework for meaningful change.`;
      return about;
    } else if (style === 'warm') {
      let about = `${story}\n\n`;
      about += `${journey}. Along the way, I've learned that ${strengths.toLowerCase()}, which has become central to how I work with clients today.\n\n`;
      if (training) {
        about += `${training}. While credentials matter, what I value most is creating a warm, safe space where you can truly be yourself.\n\n`;
      }
      about += `I offer ${services} because I'm passionate about ${purpose.toLowerCase()}. My approach is ${approach.toLowerCase()}, and I believe the best transformations happen when you feel truly seen, heard, and supported.\n\n`;
      if (specializations) {
        about += `I especially love working with ${specializations.toLowerCase()}. There's something incredibly rewarding about witnessing these journeys unfold.\n\n`;
      }
      about += `When we work together, you'll find a judgment-free space where your experiences and feelings are honored. I'm here to walk alongside you, offering both practical guidance and heartfelt support as you navigate your path forward.`;
      return about;
    } else { // mystical
      let about = `${story}\n\n`;
      about += `${journey}. Through this spiritual unfolding, I awakened to ${strengths.toLowerCase()}, gifts that now guide my sacred work with others.\n\n`;
      if (training) {
        about += `${training}. Yet I know that true wisdom flows from the divine source, and my practice is rooted in both earthly knowledge and celestial guidance.\n\n`;
      }
      about += `I channel divine wisdom through ${services}, serving those who are called to ${purpose.toLowerCase()}. My practice is ${approach.toLowerCase()}, honoring the mystical currents that flow through all existence.\n\n`;
      if (specializations) {
        about += `The Universe particularly calls me to work with ${specializations.toLowerCase()}, where the energetic alignments are most profound.\n\n`;
      }
      about += `When you enter my sacred space, you'll find yourself held in divine light and cosmic wisdom. Together, we'll journey through the veils between worlds, connecting with your higher self and the universal energies that guide your soul's evolution.`;
      return about;
    }
  };

  const handleGenerateAbout = async (retryCount = 0) => {
    setIsGenerating(true);

    try {
      let about = (await generateAiContent('about', answers, selectedStyle)).trim();

      if (!about) {
        throw new Error('Empty response from AI service');
      }

      // Validate output
      const wordCount = about.split(' ').filter(w => w).length;

      if (wordCount < 200 || wordCount > 500) {
        console.warn(`About word count out of range: ${wordCount} words`);
        // Retry once if too long/short
        throw new Error('Word count validation failed');
      }

      // Check for common issues
      if (about.toLowerCase().includes('i offer i offer') || about.toLowerCase().includes('i help i help')) {
        throw new Error('Repetition detected');
      }

      if (!about.endsWith('.') && !about.endsWith('!') && !about.endsWith('?')) {
        about += '.'; // Add period if missing
      }

      setGeneratedAbout(about);
      setStep(3);

    } catch (error) {
      console.error('Error generating about:', error);

      // Retry once automatically
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return handleGenerateAbout(1); // Retry with count
      }

      // After retry fails, use improved mock
      const mockAbout = generateMockAbout(answers, selectedStyle);
      setGeneratedAbout(mockAbout);
      setStep(3);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setIsGenerating(true);
    await handleGenerateAbout();
  };

  const handleTryAnotherStyle = () => {
    setSelectedStyle(null);
    setStep(2);
  };

  const handleUseAbout = () => {
    onAboutGenerated(generatedAbout);
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setAnswers({
      services: '', journey: '', strengths: '', purpose: '',
      training: '', story: '', approach: '', specializations: ''
    });
    setSelectedStyle(null);
    setGeneratedAbout('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 1 ? "Let's write your About section!" : step === 2 ? 'Choose your style' : 'Your About section is ready'}
              </h2>
              <p className="text-sm text-gray-600">
                Step {step} of 3
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Questions (8 questions) */}
          {step === 1 && (
            <div className="space-y-6">
              <p className="text-gray-700">
                We'll ask a few more questions to create a richer story:
              </p>

              {questions.map((question, index) => (
                <div key={question.id}>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {index + 1}. {question.label}
                    {!question.required && <span className="text-gray-500 ml-1">(Optional)</span>}
                  </label>
                  <textarea
                    value={answers[question.id]}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder={question.placeholder}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{question.helper}</p>
                </div>
              ))}

              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStyles()}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continue to Choose Style
              </button>

              {!canProceedToStyles() && (
                <p className="text-sm text-amber-600 text-center">
                  Please answer all required questions to continue
                </p>
              )}
            </div>
          )}

          {/* Step 2: Style Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-4">
                Choose the tone that best represents you:
              </p>

              {styles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`w-full text-left p-4 border-2 rounded-xl transition-all ${
                    selectedStyle === style.id
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedStyle === style.id
                        ? 'border-purple-600 bg-purple-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedStyle === style.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{style.name}</p>
                      <p className="text-sm text-gray-600">{style.description}</p>
                    </div>
                  </div>
                </button>
              ))}

              <button
                onClick={handleGenerateAbout}
                disabled={!selectedStyle || isGenerating}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating your About section...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate About Section
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Generated About */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-purple-900">
                    {styles.find(s => s.id === selectedStyle)?.name} style
                  </span>
                  <span className="text-sm text-purple-700">
                    {generatedAbout.split(' ').filter(w => w).length} words
                  </span>
                </div>
                <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                  {generatedAbout}
                </p>
              </div>

              {generatedAbout.split(' ').filter(w => w).length < 200 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    ⚠️ This about section is shorter than recommended. Consider regenerating.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleTryAnotherStyle}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Try Another Style
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors disabled:opacity-50"
                >
                  {isGenerating ? 'Generating...' : 'Regenerate'}
                </button>
              </div>

              <button
                onClick={handleUseAbout}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Use This About Section
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutGeneratorModal;
